(function () {
  'use strict';

  // Meet The Kits — view toggle, per-card accordion, arrows, and pointer drag.
  //
  // The track is a scroll-snap container rather than Flickity: the arrows only
  // need to advance one card, and this avoids depending on a section type being
  // registered in theme.js. Drag-to-scroll is added below so it behaves like
  // Flickity with a mouse; touch devices already drag natively.

  var DRAG_THRESHOLD = 5;

  function reduceMotion() {
    return window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Rewind a video to the first frame. A video with preload="metadata" is not
  // seekable until its metadata arrives, so when it is not ready yet the rewind
  // is deferred to loadedmetadata rather than silently skipped.
  function rewind(video) {
    if (video.readyState > 0) {
      try {
        video.currentTime = 0;
      } catch (e) { /* not seekable */ }
      return;
    }

    video.addEventListener('loadedmetadata', function once() {
      video.removeEventListener('loadedmetadata', once);
      try {
        video.currentTime = 0;
      } catch (e) { /* not seekable */ }
    });
  }

  /* ---------- Accordion animation ----------
     auto height cannot be transitioned, so max-height is driven from
     scrollHeight and released to 'none' once the panel is open (otherwise the
     panel would clip if its content reflowed, e.g. on resize). */

  function openPanel(panel) {
    panel.hidden = false;
    panel.classList.add('is-open');

    if (reduceMotion()) {
      panel.style.maxHeight = 'none';
      return;
    }

    window.requestAnimationFrame(function () {
      panel.style.maxHeight = panel.scrollHeight + 'px';
    });

    panel.addEventListener('transitionend', function done(event) {
      if (event.propertyName !== 'max-height') return;
      panel.removeEventListener('transitionend', done);

      if (panel.classList.contains('is-open')) {
        panel.style.maxHeight = 'none';
      }
    });
  }

  function closePanel(panel) {
    if (reduceMotion()) {
      panel.classList.remove('is-open');
      panel.style.maxHeight = '';
      return;
    }

    // Give the transition a concrete starting height to animate down from
    panel.style.maxHeight = panel.scrollHeight + 'px';

    window.requestAnimationFrame(function () {
      panel.classList.remove('is-open');
      panel.style.maxHeight = '0px';
    });
  }

  function init(section) {
    if (!section || section.dataset.kitsInit === 'true') return;
    section.dataset.kitsInit = 'true';

    var track = section.querySelector('[data-kits-track]');
    var aside = section.querySelector('.meet-kits__aside');
    var prev = section.querySelector('[data-kits-prev]');
    var next = section.querySelector('[data-kits-next]');

    /* ---------- Aside height ----------
       In the design the view toggle sits level with the bottom of the images,
       not the bottom of the whole card. The media height depends on the card
       width, so it has to be measured rather than hard-coded. */

    function syncAside() {
      if (!aside) return;

      var media = section.querySelector('.meet-kits__media');

      if (!media || window.innerWidth < 768) {
        aside.style.height = '';
        return;
      }

      var height = media.getBoundingClientRect().height;
      aside.style.height = height ? height + 'px' : '';
    }

    /* ---------- View toggle ---------- */

    function setView(view) {
      section.dataset.view = view;

      section.querySelectorAll('[data-kits-view]').forEach(function (pill) {
        var active = pill.dataset.kitsView === view;
        pill.classList.toggle('is-active', active);
        pill.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      section.querySelectorAll('[data-kits-card]').forEach(function (card) {
        var wanted = card.querySelector('[data-kits-media="' + view + '"]');
        var fallback = card.querySelector('[data-kits-media]');
        var target = wanted || fallback;

        card.querySelectorAll('[data-kits-media]').forEach(function (item) {
          var show = item === target;
          item.hidden = !show;
          item.classList.toggle('is-active', show);

          var video = item.querySelector('video');
          if (!video) return;

          if (show) {
            // Always start from the first frame when a view is selected
            rewind(video);

            var played = video.play();
            if (played && played.catch) played.catch(function () {});
          } else {
            video.pause();
            rewind(video);
          }
        });
      });
    }

    section.querySelectorAll('[data-kits-view]').forEach(function (pill) {
      pill.addEventListener('click', function () {
        setView(pill.dataset.kitsView);
      });
    });

    /* ---------- Accordion ----------
       The whole info card toggles, not just the title row. The button only
       wraps the title and the icon, so clicking the description — the largest
       part of the card — used to do nothing while still showing a pointer.
       Clicks are delegated from the card and ignored on links and on anything
       inside the open panel, so "Go To Case" and text selection still work. */

    section.querySelectorAll('[data-kits-info]').forEach(function (info) {
      var button = info.querySelector('[data-kits-accordion]');
      var panel = info.querySelector('[data-kits-panel]');

      if (!button || !panel || button.disabled) return;

      function toggle() {
        var open = button.getAttribute('aria-expanded') === 'true';

        button.setAttribute('aria-expanded', open ? 'false' : 'true');

        if (open) {
          closePanel(panel);
        } else {
          openPanel(panel);
        }

        // The design gives the expanded card more bottom padding
        info.classList.toggle('is-open', !open);
      }

      info.addEventListener('click', function (event) {
        // Links keep their own behaviour
        if (event.target.closest('a')) return;

        // Clicking inside the expanded panel should not collapse it
        if (event.target.closest('[data-kits-panel]')) return;

        toggle();
      });

      // The button is inside the card, so its native click would bubble and
      // fire the handler above — no separate listener needed. Keyboard use
      // still works because a real <button> emits click on Enter and Space.
    });

    if (!track) {
      syncAside();
      return;
    }

    /* ---------- Arrows ---------- */

    function step() {
      var card = track.querySelector('[data-kits-card]');
      if (!card) return track.clientWidth;

      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;

      return card.getBoundingClientRect().width + gap;
    }

    function syncArrows() {
      if (!prev || !next) return;

      var maxScroll = track.scrollWidth - track.clientWidth;

      // 1px tolerance: sub-pixel widths never land exactly on the end
      prev.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft >= maxScroll - 1;
    }

    if (prev && next) {
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: 'smooth' });
      });

      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', function () {
      window.requestAnimationFrame(syncArrows);
    });

    window.addEventListener('resize', function () {
      syncArrows();
      syncAside();
    });

    // Media loads after layout, so re-measure once it settles
    window.addEventListener('load', function () {
      syncAside();
      syncArrows();
    });

    if (window.ResizeObserver) {
      var media = section.querySelector('.meet-kits__media');
      if (media) new window.ResizeObserver(syncAside).observe(media);
    }

    /* ---------- Pointer drag ---------- */

    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScroll = 0;
    var pointerId = null;

    track.addEventListener('pointerdown', function (event) {
      // Mouse only — touch and pen already scroll natively
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      pointerId = event.pointerId;
    });

    track.addEventListener('pointermove', function (event) {
      if (!dragging || event.pointerId !== pointerId) return;

      var delta = event.clientX - startX;

      if (!moved && Math.abs(delta) < DRAG_THRESHOLD) return;

      if (!moved) {
        moved = true;
        track.classList.add('is-dragging');

        // Capture after the threshold, so plain clicks still reach buttons
        try { track.setPointerCapture(pointerId); } catch (e) { /* noop */ }
      }

      event.preventDefault();
      track.scrollLeft = startScroll - delta;
    });

    function swallowClick(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    function endDrag(event) {
      if (!dragging) return;
      if (event && event.pointerId != null && event.pointerId !== pointerId) return;

      dragging = false;

      if (moved) {
        try { track.releasePointerCapture(pointerId); } catch (e) { /* noop */ }

        // Let the click that follows the drag be swallowed
        track.addEventListener('click', swallowClick, true);
        window.setTimeout(function () {
          track.removeEventListener('click', swallowClick, true);
        }, 0);
      }

      moved = false;
      pointerId = null;
      track.classList.remove('is-dragging');
      syncArrows();
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    // Native image drag would fight the pointer drag
    track.addEventListener('dragstart', function (event) {
      event.preventDefault();
    });

    setView(section.dataset.view || 'exterior');
    syncAside();
    syncArrows();
  }

  function initAll() {
    document.querySelectorAll('[data-meet-kits]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: re-init when the section is reloaded
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-meet-kits]');
    if (section) init(section);
  });
})();
